import { useMemo, useState } from "react";

import {
  FiltrosAlertas as ControlesFiltros,
  type CampoFiltro,
} from "../alertas/FiltrosAlertas";
import { TabelaAlertas } from "../alertas/TabelaAlertas";
import { ModalErro } from "../alertas/ModalErro";
import { ModalReagendar } from "../alertas/ModalReagendar";
import {
  useAlertas,
  useEmpresasAlertas,
  useReagendar,
} from "../../lib/alertas/hooks";
import {
  LIMITE_DEFAULT,
  type AlertaHistorico,
  type FiltrosAlertas,
} from "../../lib/alertas/tipos";
import { useTemPermissao } from "../../lib/auth/permissoes";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Pagina do historico de alertas (FE-7 — Tela 7 §10.5).
 *
 * Orquestra: os filtros (`filtros`, com `limit = LIMITE_DEFAULT` fixo e `offset`
 * paginavel), a tabela e os dois modais (erro / reagendamento). A consulta de
 * alertas e parametrizada SO pelos filtros (a escopagem por empresa e por TOKEN no
 * backend — nao ha empresa no path). A lista de empresas vira um mapa id->rotulo
 * para a coluna Empresa.
 *
 * PAGINACAO POR OFFSET (a resposta e array PURO, sem total no corpo):
 *  - "Anterior" habilitada quando `offset > 0`;
 *  - "Proxima" habilitada quando a pagina veio CHEIA (`length === limit`);
 *  - "Mostrando X-Y" com X = offset+1 e Y = offset+itens.length (SEM total).
 *
 * FILTROS (estado limpo): so promovemos um campo a filtro ATIVO quando ele e
 * nao-vazio. Campo esvaziado e REMOVIDO do objeto (a queryKey nao carrega
 * `severidade=""`). Trocar QUALQUER filtro RESETA o `offset` para 0.
 *
 * REAGENDAR e gated por `reagendar_notificacao` (botao oculto sem a permissao) —
 * a tela ja e gated na nav por `configurar_alertas`. No sucesso da mutacao, os
 * dois modais fecham; o hook ja invalida o historico (refetch).
 */

/** Estado inicial dos filtros: so `limit`/`offset` (opcionais ausentes). */
function filtrosIniciais(): FiltrosAlertas {
  return { limit: LIMITE_DEFAULT, offset: 0 };
}

export function HistoricoAlertas(): JSX.Element {
  const [filtros, setFiltros] = useState<FiltrosAlertas>(filtrosIniciais);
  const [erroAberto, setErroAberto] = useState<AlertaHistorico | null>(null);
  const [reagendarAberto, setReagendarAberto] =
    useState<AlertaHistorico | null>(null);

  const consulta = useAlertas(filtros);
  const empresas = useEmpresasAlertas();
  const reagendar = useReagendar();
  const podeReagendar = useTemPermissao("reagendar_notificacao");

  // Mapa empresa_id -> rotulo (fantasia ?? razao_social) para a coluna Empresa.
  const rotuloPorEmpresa = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const empresa of empresas.data ?? []) {
      mapa.set(empresa.id, empresa.fantasia ?? empresa.razao_social);
    }
    return mapa;
  }, [empresas.data]);

  function resolverEmpresa(empresaId: string): string {
    return rotuloPorEmpresa.get(empresaId) ?? empresaId;
  }

  function aoMudarFiltro(campo: CampoFiltro, valor: string): void {
    setFiltros((atual) => {
      // Recompoe os filtros: opcional vazio e REMOVIDO; sempre reseta o offset.
      const proximo: FiltrosAlertas = { ...atual, offset: 0 };
      if (valor === "") {
        delete proximo[campo];
      } else {
        proximo[campo] = valor;
      }
      return proximo;
    });
  }

  function aoLimpar(): void {
    setFiltros(filtrosIniciais());
  }

  function paginaAnterior(): void {
    setFiltros((atual) => ({
      ...atual,
      offset: Math.max(0, atual.offset - atual.limit),
    }));
  }

  function proximaPagina(): void {
    setFiltros((atual) => ({ ...atual, offset: atual.offset + atual.limit }));
  }

  function abrirErro(item: AlertaHistorico): void {
    setErroAberto(item);
  }

  function abrirReagendar(item: AlertaHistorico): void {
    // Reagendar a partir do modal de erro: fecha o erro e abre o reagendamento.
    setErroAberto(null);
    setReagendarAberto(item);
  }

  function fecharModais(): void {
    setErroAberto(null);
    setReagendarAberto(null);
  }

  function confirmarReagendamento(justificativa: string): void {
    if (reagendarAberto === null) return;
    reagendar.mutate(
      { outboxId: reagendarAberto.id_outbox, justificativa },
      {
        onSuccess: () => {
          // O hook ja invalida o historico (refetch). Aqui so fechamos os modais.
          fecharModais();
        },
      },
    );
  }

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-titulo text-2xl text-off-black">
          Histórico de Alertas
        </h1>
        <p className="font-corpo text-base text-off-black">
          Acompanhe os alertas disparados e o status de entrega por canal.
        </p>
      </div>

      <ControlesFiltros
        empresaId={filtros.empresa_id ?? ""}
        severidade={filtros.severidade ?? ""}
        canal={filtros.canal ?? ""}
        status={filtros.status ?? ""}
        aoMudarFiltro={aoMudarFiltro}
        aoLimpar={aoLimpar}
      />

      <CorpoHistorico
        consulta={consulta}
        filtros={filtros}
        podeReagendar={podeReagendar}
        resolverEmpresa={resolverEmpresa}
        aoAbrirErro={abrirErro}
        aoReagendar={abrirReagendar}
        paginaAnterior={paginaAnterior}
        proximaPagina={proximaPagina}
      />

      {erroAberto !== null && (
        <ModalErro
          alerta={erroAberto}
          podeReagendar={podeReagendar}
          aoFechar={fecharModais}
          aoReagendar={() => abrirReagendar(erroAberto)}
        />
      )}

      {reagendarAberto !== null && (
        <ModalReagendar
          aoConfirmar={confirmarReagendamento}
          aoCancelar={fecharModais}
          processando={reagendar.isPending}
        />
      )}
    </section>
  );
}

interface CorpoHistoricoProps {
  consulta: ReturnType<typeof useAlertas>;
  filtros: FiltrosAlertas;
  podeReagendar: boolean;
  resolverEmpresa: (empresaId: string) => string;
  aoAbrirErro: (item: AlertaHistorico) => void;
  aoReagendar: (item: AlertaHistorico) => void;
  paginaAnterior: () => void;
  proximaPagina: () => void;
}

/**
 * Corpo do historico: trata loading/erro, renderiza a tabela + a paginacao com o
 * rotulo "Mostrando X-Y" (array puro, sem total).
 */
function CorpoHistorico({
  consulta,
  filtros,
  podeReagendar,
  resolverEmpresa,
  aoAbrirErro,
  aoReagendar,
  paginaAnterior,
  proximaPagina,
}: CorpoHistoricoProps): JSX.Element {
  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando histórico de alertas…
      </p>
    );
  }

  if (consulta.isError) {
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagemErro(consulta.error)}
      </p>
    );
  }

  const itens = consulta.data;
  // Paginacao por offset (array puro, sem total): "Proxima" so com pagina CHEIA;
  // "Anterior" so com offset para recuar.
  const temProxima = itens.length === filtros.limit;
  const temAnterior = filtros.offset > 0;
  const inicio = itens.length === 0 ? 0 : filtros.offset + 1;
  const fim = filtros.offset + itens.length;

  return (
    <div className="flex flex-col gap-4">
      <TabelaAlertas
        itens={itens}
        resolverEmpresa={resolverEmpresa}
        podeReagendar={podeReagendar}
        aoAbrirErro={aoAbrirErro}
        aoReagendar={aoReagendar}
      />

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={paginaAnterior}
          disabled={!temAnterior}
          className="rounded-md border border-platinum bg-platinum px-3 py-1 font-subtitulo text-off-black hover:text-flame disabled:opacity-60"
        >
          Anterior
        </button>
        <button
          type="button"
          onClick={proximaPagina}
          disabled={!temProxima}
          className="rounded-md border border-platinum bg-platinum px-3 py-1 font-subtitulo text-off-black hover:text-flame disabled:opacity-60"
        >
          Próxima
        </button>
        <span className="font-corpo text-base text-off-black">
          Mostrando {inicio}–{fim}
        </span>
      </div>
    </div>
  );
}

/**
 * Traduz o erro da consulta numa mensagem estavel. 503 -> indisponivel; demais
 * reusam a mensagem do `ErroHttp`. Erro nao-`ErroHttp` -> fallback generico.
 * Espelha o `mensagemErro` da Trilha/Projecao (mesma semantica).
 */
function mensagemErro(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.moduloIndisponivel) {
      return "Módulo de alertas indisponível no momento.";
    }
    return erro.message;
  }
  return "Não foi possível carregar o histórico de alertas.";
}
